import { useTheme } from "@mui/material";
import { convertToGoogleMapsLocation, type GeoLocation } from "@src/appV2/Location";
import { useEffect, useRef } from "react";

const MILES_TO_METERS_RATIO = 1609.34;

interface UseSearchRadiusCircleProps {
  map: google.maps.Map | undefined;
  radiusInMiles: number;
  locationFilter?: GeoLocation;
  enabled?: boolean;
}

/**
 * Hook to display a circle on the map indicating the search radius for job placements.
 * The circle is only shown when enabled and when radiusInMiles > 0.
 */
export function useSearchRadiusCircle(props: UseSearchRadiusCircleProps) {
  const { map, radiusInMiles, locationFilter, enabled = true } = props;
  const theme = useTheme();
  const radiusCircleRef = useRef<google.maps.Circle | undefined>(undefined);

  // Create or update the circle when radius changes
  useEffect(() => {
    if (!enabled || radiusInMiles <= 0) {
      if (radiusCircleRef.current) {
        radiusCircleRef.current.setMap(null);
        radiusCircleRef.current = undefined;
      }

      return;
    }

    if (radiusCircleRef.current) {
      radiusCircleRef.current.setRadius(radiusInMiles * MILES_TO_METERS_RATIO);
    } else {
      radiusCircleRef.current = new google.maps.Circle({
        fillColor: theme.palette.primary.main,
        fillOpacity: 0.1,
        strokeColor: theme.palette.primary.main,
        strokeOpacity: 0.5,
        strokeWeight: 2,
        radius: radiusInMiles * MILES_TO_METERS_RATIO,
      });
    }
  }, [radiusInMiles, enabled, theme.palette.primary.main]);

  // Update circle position and attach to map
  useEffect(() => {
    if (!enabled || !map || !locationFilter || !radiusCircleRef.current || radiusInMiles <= 0) {
      return;
    }

    const radiusCircle = radiusCircleRef.current;

    if (!radiusCircle.getMap()) {
      radiusCircle.setMap(map);
    }

    radiusCircle.setCenter(convertToGoogleMapsLocation(locationFilter));
  }, [locationFilter, map, radiusInMiles, enabled]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (radiusCircleRef.current) {
        radiusCircleRef.current.setMap(null);
        radiusCircleRef.current = undefined;
      }
    };
  }, []);
}
